import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Button, 
  Avatar,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import CountUp from 'react-countup';

const About: React.FC = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  
  // Helper function to get string value from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };

  // Team members data
  const teamMembers = [
    {
      name: getTranslationString('about.team.ceo.name'),
      position: getTranslationString('about.team.ceo.position'),
      description: getTranslationString('about.team.ceo.description'),
      image: 'https://randomuser.me/api/portraits/men/41.jpg'
    },
    {
      name: getTranslationString('about.team.cto.name'),
      position: getTranslationString('about.team.cto.position'),
      description: getTranslationString('about.team.cto.description'),
      image: 'https://randomuser.me/api/portraits/women/17.jpg'
    },
    {
      name: getTranslationString('about.team.coo.name'),
      position: getTranslationString('about.team.coo.position'),
      description: getTranslationString('about.team.coo.description'),
      image: 'https://randomuser.me/api/portraits/men/36.jpg'
    }
  ];
  
  // Stats data
  const stats = [
    {
      label: getTranslationString('about.stats.projects'),
      value: 250
    },
    {
      label: getTranslationString('about.stats.clients'),
      value: 120
    },
    {
      label: getTranslationString('about.stats.countries'),
      value: 15
    },
    {
      label: getTranslationString('about.stats.team'),
      value: 45
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Paper
        sx={{
          py: { xs: 6, md: 10 },
          px: 2,
          mb: 6,
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: 'url(/resources/scene1.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(1, 84, 128, 0.8)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Typography 
              component="h1"
              variant="h2"
              sx={{ 
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              }}
            >
              {getTranslationString('about.title')}
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4,
                fontWeight: 400,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.5rem' },
              }}
            >
              {getTranslationString('about.subtitle')}
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                maxWidth: '700px',
                mx: 'auto',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.7,
              }}
            >
              {getTranslationString('about.intro')}
            </Typography>
          </Box>
        </Container>
      </Paper>
      
      {/* History Section */}
      <Container maxWidth="lg" sx={{ my: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography 
              variant="h3" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              {getTranslationString('about.history.title')}
            </Typography>
            
            <Typography variant="body1" paragraph>
              {getTranslationString('about.history.description')}
            </Typography>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Box 
              component="img"
              src="/resources/about-history.jpg"
              alt="Company History"
              sx={{ 
                width: '100%',
                borderRadius: 4,
                boxShadow: '0 8px 40px rgba(0,0,0,0.1)'
              }}
            />
          </Grid>
        </Grid>
      </Container>
      
      {/* Mission & Vision */}
      <Box sx={{ bgcolor: 'background.paper', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  borderTop: '4px solid #015480',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    borderTop: '4px solid #0277bd',
                  }
                }}
              >
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: '#015480'
                  }}
                >
                  {getTranslationString('about.mission.title')}
                </Typography>
                
                <Typography variant="body1" paragraph>
                  {getTranslationString('about.mission.description')}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  borderTop: '4px solid #0277bd',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    borderTop: '4px solid #03a9f4',
                  }
                }}
              >
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: '#0277bd'
                  }}
                >
                  {getTranslationString('about.vision.title')}
                </Typography>
                
                <Typography variant="body1" paragraph>
                  {getTranslationString('about.vision.description')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Values Section */}
      <Container maxWidth="lg" sx={{ my: 10 }}>
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom
          sx={{ 
            mb: 6,
            fontWeight: 600,
            fontFamily: '"Orbitron", "Rajdhani", sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
        >
          {getTranslationString('about.values.title')}
        </Typography>
        
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                borderTop: '4px solid #015480',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#015480'
                }}
              >
                {getTranslationString('about.values.innovation')}
              </Typography>
              
              <Typography variant="body2">
                {getTranslationString('about.values.innovation.description')}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                borderTop: '4px solid #0288d1',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#0288d1'
                }}
              >
                {getTranslationString('about.values.excellence')}
              </Typography>
              
              <Typography variant="body2">
                {getTranslationString('about.values.excellence.description')}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                borderTop: '4px solid #03a9f4',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#03a9f4'
                }}
              >
                {getTranslationString('about.values.integrity')}
              </Typography>
              
              <Typography variant="body2">
                {getTranslationString('about.values.integrity.description')}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                borderTop: '4px solid #00bcd4',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#00bcd4'
                }}
              >
                {getTranslationString('about.values.collaboration')}
              </Typography>
              
              <Typography variant="body2">
                {getTranslationString('about.values.collaboration.description')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      
      {/* Team Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom
            sx={{ 
              mb: 6,
              fontWeight: 600,
              fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            {getTranslationString('about.team.title')}
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            },
            gap: 4 
          }}>
            {teamMembers.map((member, index) => (
              <Box key={index}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                    borderTop: '4px solid #015480',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                      borderTop: '4px solid #0277bd',
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', pt: '75%' }}>
                    <Avatar
                      src={member.image}
                      alt={member.name || `Team Member ${index + 1}`}
                      sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: 0
                      }}
                    />
                  </Box>
                  
                  <CardContent sx={{ p: 4 }}>
                    <Typography 
                      variant="h5" 
                      component="h3" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}
                    >
                      {member.name}
                    </Typography>
                    
                    <Typography 
                      variant="subtitle1" 
                      color="primary"
                      gutterBottom
                      sx={{ mb: 2 }}
                    >
                      {member.position}
                    </Typography>
                    
                    <Typography variant="body2">
                      {member.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
      
      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ my: 10 }}>
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom
          sx={{ 
            mb: 6,
            fontWeight: 600,
            fontFamily: '"Orbitron", "Rajdhani", sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
        >
          {getTranslationString('about.stats.title')}
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: 'repeat(2, 1fr)', 
            md: 'repeat(4, 1fr)' 
          },
          gap: 4 
        }}>
          {stats.map((stat, index) => (
            <Box key={index}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 2,
                  height: '100%',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  borderTop: '4px solid #015480',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    borderTop: '4px solid #0277bd',
                  }
                }}
              >
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#015480',
                    mb: 1
                  }}
                >
                  <CountUp 
                    start={0} 
                    end={stat.value} 
                    duration={2.5} 
                    separator="," 
                  />
                </Typography>
                
                <Typography 
                  variant="h6"
                  sx={{ 
                    fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}
                >
                  {stat.label}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>
      
      {/* CTA Section */}
      <Box 
        sx={{ 
          bgcolor: 'background.paper',
          py: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: 'linear-gradient(90deg, #015480, #0288d1)',
          },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h2"
              sx={{ 
                fontWeight: 700,
                mb: 2,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              }}
            >
              {getTranslationString('about.contact.title')}
            </Typography>
            
            <Typography 
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}
            >
              {getTranslationString('about.contact.description')}
            </Typography>
            
            <Button 
              component={Link}
              to="/#contact"
              variant="contained" 
              color="primary" 
              size="large"
              sx={{ 
                px: 4,
                py: 1.5,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                fontWeight: 500,
                letterSpacing: '0.5px',
              }}
            >
              {getTranslationString('about.contact.button')}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About; 